// /health: can the portal reach the API and its hub? Needs no sign-in.
import { useEffect, useState } from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { loadAuthConfig, loadConfig } from "../config";
import { CheckIcon, KV } from "../shared/components";
import { Card } from "../ui";

type Probe = { state: "checking" } | { state: "ok"; detail: string } | { state: "bad"; detail: string };

export function HealthPage() {
  const [api, setApi] = useState<Probe>({ state: "checking" });
  const [auth, setAuth] = useState<Probe>({ state: "checking" });
  const [hub, setHub] = useState<Probe>({ state: "checking" });
  const [apiUrl, setApiUrl] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const config = await loadConfig();
        setApiUrl(config.apiUrl);
        const started = performance.now();
        const response = await fetch(`${config.apiUrl}/health`);
        const body = (await response.json()) as { status: string; version: string };
        setApi(response.ok ? { state: "ok", detail: `version ${body.version} – ${Math.round(performance.now() - started)} ms` } : { state: "bad", detail: `HTTP ${response.status}` });
        try {
          const a = await loadAuthConfig(config.apiUrl);
          setAuth(a.enabled ? { state: "ok", detail: `Microsoft sign-in – ${new URL(a.authority!).host}` } : { state: "bad", detail: "Sign-in not configured (staff key only)" });
        } catch (e) { setAuth({ state: "bad", detail: e instanceof Error ? e.message : String(e) }); }
        const connection = new HubConnectionBuilder().withUrl(`${config.apiUrl}/hubs/tenant`).configureLogging(LogLevel.None).build();
        try {
          // Without a token the server drops the connection after negotiating; reaching that point proves the hub answers.
          await connection.start();
          setHub({ state: "ok", detail: "connected" });
          await connection.stop();
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          setHub(/negotiat|abort|closed|stopped/i.test(message) ? { state: "ok", detail: "reachable (sign in for live updates)" } : { state: "bad", detail: message });
        }
      } catch (e) {
        setApi({ state: "bad", detail: e instanceof Error ? e.message : String(e) });
      }
    })();
  }, []);
  const row = (p: Probe) => <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600, color: p.state === "ok" ? "var(--status-matched-fg)" : p.state === "bad" ? "var(--status-mismatch-fg)" : "var(--text-muted)" }}><CheckIcon r={p.state === "ok" ? "ok" : p.state === "bad" ? "no" : "na"} size={16} />{p.state === "checking" ? "Checking…" : p.detail}</span>;
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--surface-page)" }}>
      <Card padding="xl" style={{ width: "100%", maxWidth: 560, display: "flex", flexDirection: "column", gap: 16 }}>
        <img src="/logo-wordmark.png" alt="AutoVouch" style={{ height: 32, alignSelf: "flex-start" }} />
        <h1 style={{ font: "800 24px var(--font-display)", margin: 0 }}>Portal health</h1>
        <KV rows={[["Portal", <span key="p" style={{ fontWeight: 600 }}>{window.location.origin}</span>], ["API", <span key="a">{apiUrl || "…"}</span>], ["API reachable", row(api)], ["Sign-in", row(auth)], ["Live updates", row(hub)]]} columns="140px 1fr" />
      </Card>
    </div>
  );
}
